import type {SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import {useCallback} from 'react'
import {LabelPicker, type LabelPickerProps, type LabelPickerLabel} from './LabelPicker'

import {useUpdateIssueLabelsBulkMutation} from './mutations/use-update-issue-labels-bulk-mutation'

export type BulkIssueLabelPickerProps = Omit<LabelPickerProps, 'onSelectionChange' | 'labels'> &
  SharedBulkActionsItemPickerProps &
  Parameters<typeof useUpdateIssueLabelsBulkMutation>[0] & {
    labelAppliedToAll: LabelPickerLabel[]
  }

export function BulkIssueLabelPicker({
  onError,
  onCompleted,
  onMutate,
  issuesToActOn,
  labelAppliedToAll,
  ...rest
}: BulkIssueLabelPickerProps) {
  const {mutate: bulkUpdateMutate} = useUpdateIssueLabelsBulkMutation({onMutate})
  const onSelectionChange = useCallback(
    (selectedLabels: LabelPickerLabel[]) => {
      const labelsToAdd = selectedLabels.filter(l => !labelAppliedToAll.some(la => la.id === l.id))
      const labelsToRemove = labelAppliedToAll.filter(la => !selectedLabels.some(l => la.id === l.id))
      const ids = [...issuesToActOn]
      const input = {
        ids,
        addedLabelIds: labelsToAdd.map(l => l.id),
        removedLabelIds: labelsToRemove.map(l => l.id),
        labels: selectedLabels,
      }

      bulkUpdateMutate(input, {
        onSuccess: response => onCompleted?.(response.data.updateIssuesBulk.jobId),
        onError,
      })
    },
    [bulkUpdateMutate, issuesToActOn, labelAppliedToAll, onCompleted, onError],
  )

  return <LabelPicker labels={labelAppliedToAll} onSelectionChange={onSelectionChange} {...rest} />
}

try{ BulkIssueLabelPicker.displayName ||= 'BulkIssueLabelPicker' } catch {}